import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, createElementVNode as _createElementVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, vModelSelect as _vModelSelect, withDirectives as _withDirectives, normalizeClass as _normalizeClass } from "vue"

const _hoisted_1 = { class: "container-fluid" }
const _hoisted_2 = { class: "row" }
const _hoisted_3 = { class: "col-12 mt-5" }
const _hoisted_4 = { class: "table_style" }
const _hoisted_5 = { class: "d-flex justify-content-end align-items-center gap-3 mt-3 mb-4" }
const _hoisted_6 = { class: "d-flex align-items-center gap-2" }
const _hoisted_7 = ["value"]
const _hoisted_8 = { class: "pagination-mini" }
const _hoisted_9 = ["disabled"]
const _hoisted_10 = ["disabled", "onClick"]
const _hoisted_11 = ["disabled"]

import {
  ref,
  reactive,
  computed,
  onMounted,
  watch,
  defineAsyncComponent,
} from "vue";
import { useRouter } from "vue-router";
import apiService from "@/services/apiService";
import CommonTable2 from "@/components/commen/table/CommonTable2.vue";
import TableTitle from "@/components/commen/table/TableTitle.vue";
import { usePermissions } from "@/composables/usePermissions";

interface Filters {
  status: string;
}
// Filters

export default /*@__PURE__*/_defineComponent({
  __name: 'ReleaseNotes',
  setup(__props) {

const { canView, canCreate, canUpdate, canDelete } = usePermissions();

const ReleaseNoteModal = defineAsyncComponent(
  () => import("@/components/pms/admin/ReleaseNoteModal.vue")
);

const router = useRouter();
const loading = ref(false);
const rows = ref([]);
const selectedReleaseNote = ref(null);
const showModal = ref(false);
const isViewMode = ref(false);

const filters = reactive<Filters>({
  status: "",
});

// pagination
const currentPage = ref(1);
const tablePagination = ref({
  current_page: 1,
  per_page: 10,
  total: 0,
  total_pages: 0,
  has_next: false,
  has_prev: false,
});

const visiblePages = computed(() => {
  const total = tablePagination.value.total_pages;
  const current = tablePagination.value.current_page;

  // If total pages small → show all
  if (total <= 7) {
    return Array.from({ length: total }, (_, i) => i + 1);
  }

  let pages: any[] = [];

  // Always show first page
  pages.push(1);

  // Left side "..."
  if (current > 4) {
    pages.push("...");
  }

  // Middle window (current -1, current, current +1)
  const windowStart = Math.max(2, current - 1);
  const windowEnd = Math.min(total - 1, current + 1);

  for (let i = windowStart; i <= windowEnd; i++) {
    pages.push(i);
  }

  // Right side "..."
  if (current < total - 3) {
    pages.push("...");
  }

  // Always show last page
  pages.push(total);

  return pages;
});

const changeRows = () => {
  currentPage.value = 1;
  fetchReleaseNotes();
};

watch(
  () => tablePagination.value,
  (newVal) => {
    tableConfig.value.pagination = newVal;
  },
  { deep: true }
);

const resetFilters = () => {
  filters.status = "";
  currentPage.value = 1;
};

// FETCH RELEASE NOTES
const fetchReleaseNotes = async () => {
  try {
    loading.value = true;
    const params: Record<string, any> = {
      page: currentPage.value,
      limit: tablePagination.value.per_page,
      // ...filters, // If backend supports filtering
    };

    const response = await apiService.getReleaseNotes(params);
    const data = response.data || [];
    rows.value = data.map((item: any) => ({
      ...item,
      // Map for CommonTable2 if needed or keep raw
      description: item.description || '', // Keep raw HTML for editing
      description_preview: item.description ? item.description.replace(/<[^>]*>?/gm, '').substring(0, 100) + (item.description.length > 100 ? '...' : '') : '',
      version: item.release_note_version || "-",
      date: item.created_at ? new Date(item.created_at).toLocaleDateString() : "-",
    }));

    tablePagination.value = {
      current_page: response.pagination?.current_page || 1,
      per_page: params.limit,
      total: response.pagination?.total || 0,
      total_pages: response.pagination?.total_pages || 1,
      has_next: response.pagination?.has_next || false,
      has_prev: response.pagination?.has_prev || false,
    };
  } catch (error) {
    console.error("Error fetching release notes:", error);
    rows.value = [];
  } finally {
    loading.value = false;
  }
};

const openCreateModal = () => {
  selectedReleaseNote.value = null;
  isViewMode.value = false;
  showModal.value = true;
};

const editReleaseNote = (row_or_id: any) => {
  let note = null;
  if (typeof row_or_id === 'string') {
      note = rows.value.find((r: any) => r._id === row_or_id);
  } else {
      note = row_or_id; // It's the row object
  }

  if (note) {
    selectedReleaseNote.value = note;
    isViewMode.value = false;
    showModal.value = true;
  }
};

const viewReleaseNote = (note: any) => {
    selectedReleaseNote.value = note;
    isViewMode.value = true;
    showModal.value = true;
};

const closeModal = () => {
  showModal.value = false;
  selectedReleaseNote.value = null;
};

const handleSaved = () => {
  closeModal();
  fetchReleaseNotes();
};

const handleStatusFilter = (val: string) => {
  filters.status = val.toLowerCase();
  currentPage.value = 1;
};

const deleteReleaseNote = async (id: string) => {
  if (confirm("Delete this Release Note?")) {
    try {
      await apiService.deleteReleaseNote(id);
      // Small delay to ensure backend consistency
      setTimeout(() => {
        fetchReleaseNotes();
      }, 500); 
    } catch (error) {
      console.error("Error deleting note:", error);
    }
  }
};

watch(
  [filters],
  () => {
    fetchReleaseNotes();
  },
  { deep: true }
);

// Load on mount
onMounted(() => {
  fetchReleaseNotes();
});

// table config
const tableConfig = ref({
  columns: [
    { name: "Title", field: "title", sortable: true, width: "200px" },
    { name: "Description", field: "description_preview", width: "auto", isHtml: false }, 
    { name: "Major Version", field: "major_version", width: "120px" },
    { name: "Version", field: "release_note_version", width: "100px" },
    { name: "Date", field: "date", width: "150px" },
  ],
  data: rows,
  actions: [true],
  loading: loading,
  pagination: tablePagination,
  showCategoryFilter: false,
  showStatusFilter: false, 
  showResetFilter: false,
  table_width: "100%",
});

const handlePageChange = (page: any) => {
  currentPage.value = page;
  fetchReleaseNotes();
};

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("div", _hoisted_3, [
        _createElementVNode("div", null, [
          _createVNode(TableTitle, {
            title: "Release Notes",
            pageTitle: "Release Notes",
            icon: "fas fa-clipboard-list",
            showAction: true,
            actionLabel: "Create Release Note",
            onAction: openCreateModal,
            disabled: !_unref(canCreate)('website')
          }, null, 8, ["disabled"]),
          _createElementVNode("div", _hoisted_4, [
            _createVNode(CommonTable2, {
              tableObj: tableConfig.value,
              onEdit: editReleaseNote,
              onDelete: deleteReleaseNote,
              onView: viewReleaseNote,
              onStatusFilter: handleStatusFilter,
              onResetFilters: resetFilters,
              onPageChanged: handlePageChange,
              permissionKey: "website"
            }, null, 8, ["tableObj"])
          ]),
          _createElementVNode("div", _hoisted_5, [
            _createElementVNode("div", _hoisted_6, [
              _cache[3] || (_cache[3] = _createElementVNode("span", { class: "text-muted" }, "Rows per page:", -1)),
              _withDirectives(_createElementVNode("select", {
                class: "form-select form-select-sm rows-select",
                "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((tablePagination.value.per_page) = $event)),
                onChange: changeRows
              }, [
                (_openBlock(), _createElementBlock(_Fragment, null, _renderList([5, 10, 20, 50, 100], (n) => {
                  return _createElementVNode("option", {
                    key: n,
                    value: n
                  }, _toDisplayString(n), 9, _hoisted_7)
                }), 64))
              ], 544), [
                [_vModelSelect, tablePagination.value.per_page]
              ])
            ]),
            _createElementVNode("div", _hoisted_8, [
              _createElementVNode("button", {
                class: "page-btn prev_btn",
                disabled: !tablePagination.value.has_prev,
                onClick: _cache[1] || (_cache[1] = ($event: any) => (handlePageChange(tablePagination.value.current_page - 1)))
              }, " « ", 8, _hoisted_9),
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(visiblePages.value, (p, index) => {
                return (_openBlock(), _createElementBlock("button", {
                  key: index,
                  class: _normalizeClass(["page-btn", {
                  'page-active': p === tablePagination.value.current_page,
                  'disabled-dots': p === '...',
                }]),
                  disabled: p === '...',
                  onClick: ($event: any) => (p !== '...' && handlePageChange(p))
                }, _toDisplayString(p), 11, _hoisted_10))
              }), 128)),
              _createElementVNode("button", {
                class: "page-btn next_btn",
                disabled: !tablePagination.value.has_next,
                onClick: _cache[2] || (_cache[2] = ($event: any) => (handlePageChange(tablePagination.value.current_page + 1)))
              }, " » ", 8, _hoisted_11)
            ])
          ])
        ])
      ])
    ]),
    _createVNode(_unref(ReleaseNoteModal), {
      show: showModal.value,
      releaseNote: selectedReleaseNote.value,
      readonly: isViewMode.value,
      onClose: closeModal,
      onSaved: handleSaved
    }, null, 8, ["show", "releaseNote", "readonly"])
  ]))
}
}

})